import { useMemo, useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from 'src/presentations/components/atom/snackbar/Snackbar';
import Modal, { ModalInfo } from 'src/presentations/components/molecules/modal';

import IconUpload from 'src/assets/images/icons/document-uploaded.svg';
import { useInvoiceSummary } from 'src/use-cases/invoice/useInvoices';
import { useNavigate } from 'react-router-dom';
import { DeviationWorkflowAction } from 'src/infra/types/deviation';
import { useToastResult } from 'src/presentations/components/molecules/toast/useToastResult';
import { usePaymentRecalculationToastSelector } from './PaymentRecalculationToastSelector';
import { IToastResultDTO } from './types';
import { RecalculationCard } from './RecalculationCard';

const StyledModal = styled(Modal)({
  zIndex: 9999,

  '& .MuiDialog-paper': {
    maxWidth: '1000px',
    height: 'calc(100% - 64px)',
    borderRadius: '16px',

    '& .MuiDialogContent-root': {
      padding: '0',
      backgroundColor: '#F5F6FA',
    },
  },
});

export const PaymentRecalculationToast = () => {
  const [isShowDetails, setIsShowDetails] = useState(false);
  const {
    isShowNotification,
    isShowSuccessInfo,
    setIsShowSuccessInfo,
    setIsShowNotification,
    currentInvoiceId,
    currentInvoiceNumber,
    currentShipmentNumber,
    closeToast,
  } = usePaymentRecalculationToastSelector();
  const { showToastResult } = useToastResult();

  const navigate = useNavigate();

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const startAutoHideTimer = (delay: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        closeToast();
      }, delay);
    };

    if (isShowNotification && !isShowDetails) {
      startAutoHideTimer(10000);
    } else if (isShowNotification && isShowDetails) {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isShowNotification, isShowDetails, closeToast]);

  const { data: invoiceSummary } = useInvoiceSummary(currentInvoiceId || '', {
    enabled: !!currentInvoiceId && (isShowNotification || isShowSuccessInfo),
  });

  const affectedItems = useMemo(() => {
    const items: IToastResultDTO[] = [];
    if (invoiceSummary && invoiceSummary?.backgroundShipmentPerformResultDto) {
      const affectedData = invoiceSummary.backgroundShipmentPerformResultDto?.affectedShipmentAndInvoice;

      if (affectedData?.affectedInvoices?.length > 0) {
        affectedData?.affectedInvoices.forEach((item) => {
          items.push({
            invoiceId: item?.invoiceId,
            invoiceNumber: item?.invoiceNumber,
            invoiceStatus: item?.invoiceStatus,
            workflowStatus: item?.invoiceStatus === 'APPROVED' ? 'RECALCULATION_READY_FOR_PAYMENT' : 'RECALCULATION_REJECT',
          });
        });
      }
      if (affectedData?.flaggedShipmentInvoices?.length > 0) {
        affectedData?.flaggedShipmentInvoices.forEach((item) => {
          items.push({
            invoiceId: item?.invoiceId,
            invoiceNumber: item?.invoiceNumber,
            shipmentNumber: item?.shipmentNumber,
            invoiceStatus: item?.status,
            workflowStatus: `RECALCULATION_${item?.currentDeviationWorkflow}`,
            deviations: item?.deviationTypeCountMap,
          });
        });
      }
      if (affectedData?.resolvedShipmentInvoices?.length > 0) {
        affectedData?.resolvedShipmentInvoices.forEach((item) => {
          items.push({
            invoiceId: item?.invoiceId,
            invoiceNumber: item?.invoiceNumber,
            shipmentNumber: item?.shipmentNumber,
            invoiceStatus: item?.status,
            workflowStatus: `RECALCULATION_RESOLVED`,
            deviations: item?.deviationTypeCountMap,
          });
        });
      }
    }
    return items;
  }, [invoiceSummary]);

  const onCloseSuccessInfo = useCallback(() => {
    setIsShowSuccessInfo(false);

    setTimeout(() => {
      const navigateUrl = `/dashboard?provider=${invoiceSummary?.serviceProvider}&modality=${invoiceSummary?.modality}`;
      navigate(navigateUrl);
    }, 500);

    if (affectedItems?.length > 0) {
      setIsShowNotification(true);
    } else {
      showToastResult(currentInvoiceId, DeviationWorkflowAction.SEND_TO_OPERATION, currentInvoiceNumber, currentShipmentNumber);
    }
  }, [
    affectedItems,
    setIsShowSuccessInfo,
    invoiceSummary?.serviceProvider,
    invoiceSummary?.modality,
    navigate,
    setIsShowNotification,
    currentInvoiceId,
    currentInvoiceNumber,
    currentShipmentNumber,
    showToastResult,
  ]);

  return (
    <>
      {isShowNotification && affectedItems?.length > 0 && (
        <Snackbar
          zIndex={9999}
          autoSidebarPlacement
          icon={<img src={IconUpload} alt='' />}
          content={<div className='flex items-center'>{affectedItems?.length ?? 0} Shipments impacted after payment recalculations</div>}
          showDivider
          actions={
            <>
              <button type='button' className='flex items-center gap-10 text-green-70 font-semibold' onClick={() => setIsShowDetails(true)}>
                Show Details
              </button>
              <button type='button' aria-label='Close' onClick={() => closeToast()}>
                <CloseIcon sx={{ color: '#808080' }} />
              </button>
            </>
          }
        />
      )}
      <StyledModal
        fullWidth
        maxWidth='md'
        open={isShowDetails}
        onClose={() => {
          setIsShowDetails(false);
        }}
      >
        <div className='relative bg-neutral-10 flex flex-col h-full'>
          <div className='px-24 pb-20 pt-24 border-b border-neutral-20 bg-white'>
            <h3 className='text-18 font-bold mt-12'>{affectedItems?.length ?? 0} Shipments Impacted after Payment Recalculations</h3>
            <p className='text-14 text-blue_grey-80 font-medium mt-5'> </p>
          </div>

          <div className='flex-1 p-24 overflow-auto flex flex-col gap-10'>
            {affectedItems.map((item) => (
              <div key={`affected-item-${item?.invoiceId}`} className=''>
                <RecalculationCard {...item} />
              </div>
            ))}
          </div>

          <div className='mt-auto px-24 py-20 border-t border-neutral-20 flex items-center justify-end gap-12 bg-white'>
            <Button
              type='button'
              variant='outlined'
              color='inherit'
              onClick={() => {
                setIsShowDetails(false);
              }}
              sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf' }}
            >
              Close
            </Button>
          </div>
        </div>
      </StyledModal>

      <ModalInfo open={isShowSuccessInfo} title='Success!' onClose={onCloseSuccessInfo}>
        <p>Successfully approved this invoice. You can now find this invoice in the Report &amp; Anaytics.</p>
      </ModalInfo>
    </>
  );
};
